/**
 * Explicit SSOT breakdown for POST /getGimmonixPaymentUrl — matches BE validator fields.
 */

import {
  normalizePaymentUsdToIlsRate,
} from "./normalizePaymentUsdToIlsRate";

export const PAYMENT_LINK_SSOT_TRACE = "[PAYMENT_LINK_SSOT_TRACE]";
export const PAYMENT_LINK_HANDLING_FEE_TRACE = "[PAYMENT_LINK_HANDLING_FEE_TRACE]";

/**
 * When actualAmount == hotelBase + handling (+ interest), handling is additive — not embedded in base.
 * @param {{
 *   hotelBaseAmountIls: number,
 *   handlingFeeIls?: number,
 *   interestFeeIls?: number,
 *   amount?: number,
 *   actualAmount?: number,
 *   explicitFlag?: boolean,
 * }} ctx
 * @returns {boolean}
 */
export function resolveHandlingFeeIncludedInTotalForPaymentLink({
  hotelBaseAmountIls,
  handlingFeeIls = 0,
  interestFeeIls = 0,
  amount,
  actualAmount,
  explicitFlag = false,
}) {
  const base = Math.ceil(Number(hotelBaseAmountIls) || 0);
  const handling = Math.max(0, Math.ceil(Number(handlingFeeIls) || 0));
  const interest = Math.max(0, Number(interestFeeIls) || 0);
  const charge = Number(actualAmount ?? amount ?? 0);
  if (!Number.isFinite(charge) || charge <= 0) {
    return Boolean(explicitFlag);
  }
  const tol = Math.max(2, charge * 0.005);
  const additiveTotal = base + handling + interest;
  if (handling > 0 && Math.abs(charge - additiveTotal) <= tol) {
    return false;
  }
  const baseOnlyTotal = base + interest;
  if (Math.abs(charge - baseOnlyTotal) <= Math.max(1, tol)) {
    return true;
  }
  return Boolean(explicitFlag);
}

/**
 * @param {{
 *   paymentExchangeRate?: number,
 *   bookingExchangeRate?: number,
 *   freshOrder?: Record<string, unknown> | null,
 *   orderReqData?: Record<string, unknown> | null,
 * }} ctx
 * @returns {number | null}
 */
export function resolveGimmonixPaymentExchangeRateForLink({
  paymentExchangeRate = 0,
  bookingExchangeRate = 0,
  freshOrder = null,
  orderReqData = null,
}) {
  const screenRate = normalizePaymentUsdToIlsRate(
    Number(paymentExchangeRate) || Number(bookingExchangeRate) || 0
  );
  if (screenRate != null && screenRate > 0) return screenRate;

  const snap =
    freshOrder?.booking_currency_snapshot &&
    typeof freshOrder.booking_currency_snapshot === "object"
      ? freshOrder.booking_currency_snapshot
      : orderReqData?.booking_currency_snapshot &&
          typeof orderReqData.booking_currency_snapshot === "object"
        ? orderReqData.booking_currency_snapshot
        : null;
  const fromSnap = normalizePaymentUsdToIlsRate(snap?.exchange_rate_at_booking);
  if (fromSnap != null && fromSnap > 0) return fromSnap;

  const pc =
    freshOrder?.pricecalculations ||
    freshOrder?.priceCalculations ||
    orderReqData?.pricecalculations ||
    orderReqData?.priceCalculations;
  const pcObj = pc && typeof pc === "object" ? pc : null;
  const fromPc = normalizePaymentUsdToIlsRate(
    pcObj?.current_rate ?? pcObj?.saved_rate
  );
  if (fromPc != null && fromPc > 0) return fromPc;

  return null;
}

/**
 * @param {{
 *   baseHotelPriceIls: number,
 *   handlingFeeIls: number,
 *   interestFeeIls?: number,
 *   paymentExchangeRate?: number | null,
 *   handlingFeeIncludedInTotal?: boolean,
 * }} breakdown
 */
export function buildGimmonixPaymentLinkSsotPayload({
  baseHotelPriceIls,
  handlingFeeIls,
  interestFeeIls = 0,
  paymentExchangeRate = null,
  handlingFeeIncludedInTotal = false,
}) {
  const payload = {
    hotelBaseAmountIls: baseHotelPriceIls,
    handlingFeeIls,
    handlingFeeIncludedInTotal,
    interestFeeIls: Math.max(0, Number(interestFeeIls) || 0),
  };
  if (paymentExchangeRate != null && Number(paymentExchangeRate) > 0) {
    const normalizedRate = normalizePaymentUsdToIlsRate(paymentExchangeRate);
    if (normalizedRate != null && normalizedRate > 0) {
      payload.paymentExchangeRate = normalizedRate;
    }
  }
  return payload;
}

/** @param {Record<string, unknown>} payload */
export function logPaymentLinkSsotTraceFe(payload) {
  if (typeof import.meta !== "undefined" && !import.meta.env?.DEV) return;
  try {
    // eslint-disable-next-line no-console
    console.info(PAYMENT_LINK_SSOT_TRACE, payload);
  } catch {
    /* ignore */
  }
}

/**
 * @param {{
 *   hotelBaseAmountIls: number,
 *   handlingFeeIls: number,
 *   amount: number,
 *   actualAmount: number,
 *   handlingFeeIncludedInTotal: boolean,
 *   interestFeeIls?: number,
 * }} ctx
 */
export function buildPaymentLinkHandlingFeeTraceFe({
  hotelBaseAmountIls,
  handlingFeeIls,
  amount,
  actualAmount,
  handlingFeeIncludedInTotal,
  interestFeeIls = 0,
}) {
  const base = Math.ceil(Number(hotelBaseAmountIls) || 0);
  const handling = Math.max(0, Math.ceil(Number(handlingFeeIls) || 0));
  const interest = Math.max(0, Number(interestFeeIls) || 0);
  const expectedTotalIls = base + handling + interest;
  const providerAmount = Number(amount) || 0;
  const actual = Number(actualAmount) || 0;
  const tol = Math.max(1, expectedTotalIls * 0.005);
  const pass =
    Math.abs(providerAmount - expectedTotalIls) <= tol &&
    Math.abs(actual - expectedTotalIls) <= tol;
  return {
    hotelBaseAmountIls: base,
    handlingFeeIls: handling,
    amount: providerAmount,
    actualAmount: actual,
    handlingFeeIncludedInTotal,
    expectedTotalIls,
    providerAmount,
    status: pass ? "PASS" : "FAIL",
  };
}

/** @param {Record<string, unknown>} payload */
export function logPaymentLinkHandlingFeeTraceFe(payload) {
  if (typeof import.meta !== "undefined" && !import.meta.env?.DEV) return;
  try {
    // eslint-disable-next-line no-console
    console.info(PAYMENT_LINK_HANDLING_FEE_TRACE, payload);
  } catch {
    /* ignore */
  }
}
